import { yupResolver } from '@hookform/resolvers/yup';
import { Plus, Trash01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useCallback, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ImageIcon from 'src/assets/img/image-icon.svg';
import ButtonCmp from 'src/components/ButtonCmp';
import CommonModal from 'src/components/CommonModal';
import InputText from 'src/components/InputText/InputText';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { uploadLegDocument } from 'src/services/OrderService';
import { convertToGBMBKB } from 'src/utils/CommonFunctions';
import { isFileContentValid } from 'src/utils/fileIntegrity';
import WalToast from 'src/utils/WalToast';
import * as Yup from 'yup';

import pdf from '../../../assets/img/pdf-icon.png';

// Client cap for customs documents (2026-10-09). Declared once so the dropzone limit, the
// rejection message and the hint under the Add File button can never drift apart.
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// Shown under the upload box rather than as a toast, like every other rejection in this modal.
const CORRUPTED_FILE_MESSAGE = 'Corrupted file is not allowed to upload';

const schema = Yup.object().shape({
    title: Yup.string().trim().required('Title is required'),
    file: Yup.mixed().required('File is required'),
});

interface IProps {
    handleClose: () => void;
    orderLegId?: any;
    handleOnSubmit?: (data: any) => void;
}

const UploadCustomsDocModal = ({ handleClose, orderLegId, handleOnSubmit }: IProps) => {
    const { t } = useTranslation();
    const [errorMessage, setErrorMessage] = useState('');
    const [displayDocument, setDisplayDocument] = useState('');
    const [fileName, setFileName] = useState('');
    const [fileSize, setFileSize] = useState<number>(0);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const fileNameRef = useRef<HTMLParagraphElement>(null);

    const {
        control,
        handleSubmit,
        setValue,
        clearErrors,
        formState: { errors },
        getValues,
    } = useForm({
        resolver: yupResolver<any>(schema),
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
    });

    const resetFileState = useCallback(() => {
        setDisplayDocument('');
        setFileName('');
        setFileSize(0);
        setUploadProgress(0);
        setValue('file', null);
    }, [setValue]);

    const onDrop = useCallback(
        async (acceptedFiles: File[]) => {
            const file = acceptedFiles[0];
            setErrorMessage('');
            clearErrors('file');

            if (!file) {
                setErrorMessage('No file selected');

                return;
            }

            // The dropzone's maxSize normally catches this first, so this only fires if a file
            // reaches onDrop another way — keeps the cap enforced in one place that always runs.
            if (file.size > MAX_FILE_SIZE_BYTES) {
                setErrorMessage(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit`);
                resetFileState();

                return;
            }

            // A 0-byte file is treated as corrupted — GCS/back-end will reject it and the user has no useful preview
            if (file.size === 0) {
                setErrorMessage(CORRUPTED_FILE_MESSAGE);
                resetFileState();

                return;
            }

            const isValidType = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'application/pdf'].includes(file.type);

            if (!isValidType) {
                setErrorMessage('Invalid file type');

                return;
            }

            // `file.type` above comes from the EXTENSION, so renaming anything to .jpg clears every
            // check up to here — the 2MB of random bytes that prompted this read as image/jpeg and
            // uploaded happily. This looks at the actual bytes (signature, and for images whether the
            // browser can decode them), which is the only way to catch it client-side.
            if (!(await isFileContentValid(file))) {
                setErrorMessage(CORRUPTED_FILE_MESSAGE);
                resetFileState();

                return;
            }

            const reader = new FileReader();
            reader.onloadstart = resetFileState;

            reader.onloadend = () => {
                setFileName(file.name);
                setFileSize(file.size);
                setDisplayDocument(reader.result as string);
                setUploadProgress(100);
                setValue('file', file);

                // Prefill Title from the file name (extension stripped) when the user hasn't typed
                // one — the file name is almost always what they'd type. Only when empty, so a
                // title entered first survives re-picking the file. Same as WAL's upload modal.
                if (!getValues('title')) {
                    setValue('title', file.name.replace(/\.[^/.]+$/, ''));
                    clearErrors('title');
                }
            };

            // FileReader errors out when the file is unreadable (corrupt, locked, removed mid-read)
            reader.onerror = () => {
                setErrorMessage(CORRUPTED_FILE_MESSAGE);
                resetFileState();
            };
            reader.readAsDataURL(file);
        },
        [setValue, clearErrors, resetFileState]
    );

    const onDropRejected = useCallback(
        (fileRejections: any[]) => {
            const error = fileRejections[0]?.errors?.[0];
            const messages: Record<string, string> = {
                'file-invalid-type': 'Invalid file type',
                'too-many-files': 'Only one file can be uploaded at a time',
                'file-too-large': `File size exceeds ${MAX_FILE_SIZE_MB}MB limit`,
            };
            setErrorMessage(messages[error?.code] || error?.message || 'File upload failed');
            clearErrors('file');
        },
        [clearErrors]
    );

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        onDropRejected,
        maxFiles: 1,
        multiple: false,
        maxSize: MAX_FILE_SIZE_BYTES,
        accept: {
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/svg+xml': ['.svg'],
            'application/pdf': ['.pdf'],
        },
    });

    const resetDocument = () => {
        resetFileState();
        setErrorMessage('');
        // Title is prefilled from the file name, so removing the file must clear it too —
        // otherwise the next file silently keeps the previous one's name (the prefill only
        // fills an EMPTY title). Cleared unconditionally, same as WAL's upload modal.
        setValue('title', '');
        clearErrors('title');

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const onSubmit = (formData: any) => {
        if (!displayDocument || !fileName) {
            setErrorMessage('Please select a valid file to upload');

            return;
        }

        const appendFormData = new FormData();
        appendFormData.append('title', formData.title.trim());
        appendFormData.append('document', formData.file);

        setIsLoading(true);
        setErrorMessage('');

        uploadLegDocument(orderLegId, appendFormData)
            .then((response: any) => {
                if (response?.data || response) {
                    WalToast.success('Your document has been uploaded successfully');
                    if (handleOnSubmit) handleOnSubmit(response?.data || response);
                    handleClose();
                } else {
                    setErrorMessage('File upload failed');
                }
            })
            .catch((err: any) => {
                setErrorMessage(err?.response?.data?.message || err?.message || 'Error while uploading document');
            })
            .finally(() => setIsLoading(false));
    };

    const isPrimaryBtnDisabled = isLoading || !getValues('title') || !getValues('file');

    return (
        <CommonModal
            title={t('Orders.Upload_Customs_Documents')}
            handleClose={() => {
                if (isLoading) return;
                handleClose();
            }}
            size="max-w-[600px]"
            primaryBtnText={t('Orders.Attach_File')}
            secondaryBtnText={t('Common.Cancel')}
            secondaryBtnOnClick={handleClose}
            primaryBtnOnClick={handleSubmit(onSubmit)}
            primaryBtnDisabled={isPrimaryBtnDisabled}
            primaryBtnLoading={isLoading}
            secondaryBtnDisabled={isLoading}
        >
            <div className="p-4 flex flex-col gap-y-3">
                <div>
                    {!displayDocument && (
                        <>
                            <div className="flex justify-between">
                                <h6 className="form_label block mb-1.5">
                                    {t('Orders.Upload_attachment')}
                                    <span className="text-red-600 ms-[2px] leading-4 text-xs">*</span>
                                </h6>
                            </div>

                            <div className="flex gap-4" {...getRootProps()}>
                                <label className={`flex-auto upload-file-box ${errors?.file || errorMessage ? 'upload-file-box-error' : ''}`} htmlFor="fileSelect">
                                    <ButtonCmp className="btn_secondary_black pointer-events-none" icon={<Plus className="w-4 h-4" />}>
                                        {t('Common.Add_File')}
                                    </ButtonCmp>
                                    <p className="upload-file-box-text">
                                        {t('Common.File_type_With_PDF_desc')} ({t('Common.Max_Limit_desc', { size: MAX_FILE_SIZE_MB })})
                                    </p>
                                </label>

                                <input type="file" className="hidden" accept=".png,.jpg,.jpeg,.svg,.pdf" ref={fileInputRef} {...getInputProps()} />
                            </div>

                            {(errorMessage || errors?.file) && <p className="text-red-500 text-xs mt-1.5">{errorMessage}</p>}
                        </>
                    )}

                    {displayDocument && (
                        <div className="rounded-xl border border-utilityGray200 p-4 flex gap-x-3">
                            <div>
                                {fileName.endsWith('.pdf') ? (
                                    <img src={pdf} className="w-10 object-contain" alt="PDF Icon" />
                                ) : (
                                    <img src={ImageIcon} className="w-10 object-contain" alt="Image Icon" />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <TruncateTextCmp message={fileName} refName={fileNameRef}>
                                    <p ref={fileNameRef} className="text-textSecondary text-sm font-medium truncate">
                                        {fileName}
                                    </p>
                                </TruncateTextCmp>
                                <p className="text-grayLight600 text-sm font-normal">
                                    {convertToGBMBKB(fileSize)} - {uploadProgress}% {t('Orders.uploaded')}
                                </p>
                            </div>
                            <TooltipCmp message={t('Common.Delete')}>
                                <Trash01
                                    className="w-4 h-4 cursor-pointer"
                                    onClick={() => {
                                        resetDocument();
                                    }}
                                />
                            </TooltipCmp>
                        </div>
                    )}
                </div>

                <Controller
                    name="title"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <InputText
                            label={t('Common.Title')}
                            className={`form_control ${error ? 'border border-red-500 border-solid' : ''}`}
                            labelClassName="block mb-1.5"
                            placeholder={t('Common.Enter_title')}
                            value={value}
                            onChangeFunc={(e) => {
                                const inputValue = e?.target?.value || '';

                                // Spaces alone are not a title: collapse to '' so Attach File stays
                                // disabled rather than accepting a blank-looking document name.
                                onChange(inputValue.trim() === '' ? '' : inputValue.trimStart());
                                clearErrors('title');
                            }}
                            required
                        />
                    )}
                />
            </div>
        </CommonModal>
    );
};

export default UploadCustomsDocModal;
